import { useState } from "react"
import { Plus } from "@untitledui/icons"
import { toast } from "sonner"

import type { ApiEstimateImage } from "@/lib/api/types"
import { ALL_SECTIONS, componentLabel, sideBucket } from "@/lib/estimates/catalog"
import { ComponentPicker } from "./component-picker"
import { PhotoViewer } from "./photo-viewer"
import { TextSelect } from "@/components/ui/text-select"
import { type CanvasDispatch, type CanvasLine, type ComponentPick, type LineAction } from "./use-canvas"

// By photo: one photo, and the parts on it, side by side. Add a part to
// this photo, remove a part that is wrong. Nothing else on the screen.

const sectionIdx = (s: string) => {
  const i = ALL_SECTIONS.indexOf(s as (typeof ALL_SECTIONS)[number])
  return i < 0 ? 99 : i
}

export function ByPhoto({
  images,
  lines,
  dispatch,
}: {
  images: ApiEstimateImage[]
  lines: CanvasLine[]
  dispatch: CanvasDispatch
}) {
  const [index, setIndex] = useState(0)
  const [adding, setAdding] = useState(false)
  const current = images[Math.min(index, Math.max(0, images.length - 1))] ?? null
  const currentIdx = current ? images.indexOf(current) : 0

  const onPhoto = current
    ? lines
        .filter((l) => l.photos.some((p) => p.imageId === current.id))
        .sort((a, b) => sectionIdx(a.section) - sectionIdx(b.section) || a.component.localeCompare(b.component))
    : []

  const onPick = (pick: ComponentPick) => {
    dispatch({ t: "add", pick, imageId: current?.id ?? null })
    setAdding(false)
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_460px]">
      <PhotoViewer images={images} index={currentIdx} onIndex={setIndex} hug thumb="lg" />

      {current && (
        <section aria-label={`Parts on photo ${currentIdx + 1}`} className="rounded-xl border border-zinc-200 bg-white lg:sticky lg:top-5">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <h2 className="text-[15px] font-semibold text-zinc-900">
              On photo {currentIdx + 1}
              <span className="ml-1.5 font-normal text-zinc-500">· {onPhoto.length} {onPhoto.length === 1 ? "part" : "parts"}</span>
            </h2>
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-[13.5px] font-semibold text-white hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/40 focus-visible:ring-offset-2"
            >
              <Plus className="size-4" aria-hidden />
              Add a part
            </button>
          </div>

          {onPhoto.length === 0 ? (
            <p className="border-t border-zinc-100 px-4 py-8 text-center text-[13.5px] text-zinc-500">
              Nothing on this photo yet.
            </p>
          ) : (
            <ul className="divide-y divide-zinc-100 border-t border-zinc-100">
              {onPhoto.map((line) => {
                const label = componentLabel(line.component)
                return (
                  <li key={line.key} className="flex items-center gap-2 py-2 pl-4 pr-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14.5px] font-semibold text-zinc-900">{label}</span>
                      <span className="block text-[12.5px] text-zinc-500">
                        {line.addedBy === "ai" ? "Found by AI" : "Added by you"}
                      </span>
                    </span>
                    <TextSelect
                      ariaLabel={`${label}: repair or replace`}
                      value={line.action}
                      onChange={(v) => dispatch({ t: "update", key: line.key, patch: { action: v as LineAction } })}
                      options={[
                        { value: "REPAIR", label: "Repair" },
                        { value: "REPLACE", label: "Replace" },
                      ]}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        dispatch({ t: "remove", key: line.key })
                        toast(`${label} removed`, { action: { label: "Undo", onClick: () => dispatch({ t: "restore", line }) } })
                      }}
                      className="h-9 shrink-0 rounded-md px-2.5 text-[13px] font-semibold text-zinc-600 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700/30"
                      aria-label={`Remove ${label} from the estimate`}
                    >
                      Remove
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      <ComponentPicker
        open={adding}
        title={current ? `Add a part to photo ${currentIdx + 1}` : "Add a part"}
        side={current ? sideBucket(current.side) : "FRONT_CENTER"}
        askSide={!current}
        lines={lines}
        onPick={onPick}
        onClose={() => setAdding(false)}
      />
    </div>
  )
}
